// Admin Notifications Tab Component
import React, { useState } from 'react';
import { GroupBox } from '../shared/GroupBox';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAdmin, API_URL } from './AdminContext';

export function AdminNotificationsTab() {
  const { pushNotifications, fetchNotifications } = useAdmin();
  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    type: 'info',
    tenant_ids: null
  });

  const handleSendNotification = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newNotification)
      });
      if (response.ok) {
        toast.success('Notification sent');
        setNewNotification({ title: '', message: '', type: 'info', tenant_ids: null });
        fetchNotifications();
      }
    } catch (error) {
      toast.error('Failed to send notification');
    }
  };

  return (
    <div className="space-y-4">
      <GroupBox title="Send Notification" className="border-purple-500/50">
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Title</Label>
              <Input 
                value={newNotification.title}
                onChange={(e) => setNewNotification({...newNotification, title: e.target.value})}
                placeholder="Notification title"
              />
            </div>
            <div>
              <Label className="text-sm">Type</Label>
              <Select 
                value={newNotification.type}
                onValueChange={(v) => setNewNotification({...newNotification, type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-sm">Message</Label>
            <Input 
              value={newNotification.message}
              onChange={(e) => setNewNotification({...newNotification, message: e.target.value})}
              placeholder="Notification message"
            />
          </div>
          <div className="flex items-center gap-3">
            <Switch 
              checked={newNotification.tenant_ids === null}
              onCheckedChange={(checked) => setNewNotification({
                ...newNotification, 
                tenant_ids: checked ? null : []
              })}
            />
            <Label className="text-sm">Broadcast to all tenants</Label>
          </div>
          <Button onClick={handleSendNotification}>
            <Send className="w-4 h-4 mr-2" />
            Send Notification
          </Button>
        </div>
      </GroupBox>

      <GroupBox title="Recent Notifications" className="border-yellow-500/50">
        <div className="p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2">Date</th>
                <th className="text-left py-2">Title</th>
                <th className="text-left py-2">Message</th>
                <th className="text-center py-2">Type</th>
                <th className="text-center py-2">Target</th>
              </tr>
            </thead>
            <tbody>
              {pushNotifications.map((notif) => (
                <tr key={notif.notification_id} className="border-b border-gray-800">
                  <td className="py-2 text-gray-400">{new Date(notif.created_at).toLocaleString()}</td>
                  <td className="py-2">{notif.title}</td>
                  <td className="py-2 text-gray-400 truncate max-w-[200px]">{notif.message}</td>
                  <td className="py-2 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      notif.type === 'success' ? 'bg-green-900/30 text-green-400' :
                      notif.type === 'warning' ? 'bg-yellow-900/30 text-yellow-400' :
                      notif.type === 'error' ? 'bg-red-900/30 text-red-400' :
                      'bg-blue-900/30 text-blue-400'
                    }`}>
                      {notif.type}
                    </span>
                  </td>
                  <td className="py-2 text-center">
                    {notif.is_broadcast ? 
                      <span className="text-purple-400">Broadcast</span> : 
                      <span className="text-gray-400">Targeted</span>
                    }
                  </td>
                </tr>
              ))}
              {pushNotifications.length === 0 && (
                <tr>
                  <td colSpan="5" className="py-8 text-center text-gray-500">No notifications sent yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </GroupBox>
    </div>
  );
}
